import { Component, createRef } from 'react';
import raf, { cancel as caf } from 'raf';
import css from 'dom-css';

import {
  ScrollContainer,
  ScrollView,
  ScrollTrack,
  ScrollThumb,
} from './components';

import { getInnerHeight, getScrollbarWidth, returnFalse } from '../utils';

class Scrollbar extends Component {
  nativeScrollbarWidth = getScrollbarWidth();

  view = createRef();

  track = createRef();

  thumb = createRef();

  componentDidMount() {
    this.view.current.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleWindowResize);
    this.rafUpdate();

    if (!this.nativeScrollbarWidth) return;

    this.track.current.addEventListener('mousedown', this.handleTrackMouseDown);
    this.thumb.current.addEventListener('mousedown', this.handleThumbMouseDown);
  }

  componentDidUpdate() {
    this.rafUpdate();
  }

  componentWillUnmount() {
    this.view.current.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleWindowResize);
    caf(this.requestFrame);
    clearInterval(this.detectScrollingInterval);

    if (!this.nativeScrollbarWidth) return;

    this.track.current.removeEventListener(
      'mousedown',
      this.handleTrackMouseDown
    );
    this.thumb.current.removeEventListener(
      'mousedown',
      this.handleThumbMouseDown
    );
    this.teardownDragging();
  }

  getValues = () => {
    const { scrollTop = 0, scrollHeight = 0, clientHeight = 0 } =
      this.view.current || {};

    return {
      top: scrollTop / (scrollHeight - clientHeight) || 0,
      scrollTop,
      scrollHeight,
      clientHeight,
    };
  };

  getThumbHeight = () => {
    const { scrollHeight, clientHeight } = this.view.current;
    const { thumbMinSize = 30 } = this.props;
    const trackHeight = getInnerHeight(this.track.current);
    const height = Math.ceil((clientHeight / scrollHeight) * trackHeight);
    if (trackHeight === height) return 0;
    return Math.max(height, thumbMinSize);
  };

  getScrollTop() {
    return this.view.current.scrollTop;
  }

  getScrollHeight() {
    return this.view.current.scrollHeight;
  }

  getClientHeight() {
    return this.view.current.clientHeight;
  }

  getScrollTopForOffset = offset => {
    const { scrollHeight, clientHeight } = this.view.current;
    const trackHeight = getInnerHeight(this.track.current);
    const thumbHeight = this.getThumbHeight();
    return (
      (offset / (trackHeight - thumbHeight)) * (scrollHeight - clientHeight)
    );
  };

  scrollTop = (top = 0) => {
    this.view.current.scrollTop = top;
  };

  setupDragging = () => {
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.handleDragEnd);
    document.onselectstart = returnFalse;
  };

  teardownDragging = () => {
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.handleDragEnd);
    document.onselectstart = undefined;
  };

  detectScrolling = () => {
    if (this.scrolling) return;

    this.scrolling = true;
    this.handleScrollStart();
    this.detectScrollingInterval = setInterval(() => {
      if (this.lastViewScrollTop === this.viewScrollTop) {
        clearInterval(this.detectScrollingInterval);
        this.scrolling = false;
        this.handleScrollStop();
      }
      this.lastViewScrollTop = this.viewScrollTop;
    }, 100);
  };

  raf(callback) {
    if (this.requestFrame) raf.cancel(this.requestFrame);
    this.requestFrame = raf(() => {
      this.requestFrame = undefined;
      callback();
    });
  }

  rafUpdate(callback) {
    this.raf(() => this.update(callback));
  }

  update = callback => {
    const { onUpdate } = this.props;
    const values = this.getValues();

    if (this.nativeScrollbarWidth) {
      const { scrollTop, clientHeight, scrollHeight } = values;
      const trackHeight = getInnerHeight(this.track.current);
      const thumbHeight = this.getThumbHeight();
      const thumbY =
        (scrollTop / (scrollHeight - clientHeight)) *
        (trackHeight - thumbHeight);
      const thumbStyle = {
        height: thumbHeight,
        transform: `translateY(${thumbY}px)`,
      };

      css(this.thumb.current, thumbStyle);
    }

    if (onUpdate) onUpdate(values);
    if (typeof callback === 'function') callback(values);
  };

  handleWindowResize = () => {
    this.rafUpdate();
  };

  handleScrollStart = () => {
    const { onScrollStart } = this.props;
    if (onScrollStart) onScrollStart();
  };

  handleScrollStop = () => {
    const { onScrollStop } = this.props;
    if (onScrollStop) onScrollStop();
  };

  handleScroll = event => {
    const { onScroll, onScrollFrame } = this.props;
    if (onScroll) onScroll(event);
    this.rafUpdate(values => {
      const { scrollTop } = values;
      this.viewScrollTop = scrollTop;
      if (onScrollFrame) onScrollFrame(values);
    });
    this.detectScrolling();
  };

  handleDragStart = event => {
    this.dragging = true;
    event.stopImmediatePropagation();
    this.setupDragging();
  };

  handleDrag = event => {
    if (!this.prevPageY) {
      return false;
    }

    const { clientY } = event;
    const { top: trackTop } = this.track.current.getBoundingClientRect();
    const thumbHeight = this.getThumbHeight();
    const clickPosition = thumbHeight - this.prevPageY;
    const offset = -trackTop + clientY - clickPosition;
    this.view.current.scrollTop = this.getScrollTopForOffset(offset);
  };

  handleDragEnd = () => {
    this.dragging = false;
    this.prevPageY = 0;
    this.teardownDragging();
  };

  handleThumbMouseDown = event => {
    event.preventDefault();
    this.handleDragStart(event);
    const { target, clientY } = event;
    const { offsetHeight } = target;
    const { top } = target.getBoundingClientRect();
    this.prevPageY = offsetHeight - (clientY - top);
  };

  handleTrackMouseDown = event => {
    event.preventDefault();
    const { target, clientY } = event;
    const { top: targetTop } = target.getBoundingClientRect();
    const thumbHeight = this.getThumbHeight();
    const offset = Math.abs(targetTop - clientY) - thumbHeight / 2;
    this.view.current.scrollTop = this.getScrollTopForOffset(offset);
  };

  render() {
    const { width, viewClassName, containerClassName } = this.props;
    return (
      <ScrollContainer className={containerClassName}>
        <ScrollView
          nodeRef={this.view}
          scrollbarWidth={this.nativeScrollbarWidth}
          className={viewClassName}
        >
          {this.props.children}
        </ScrollView>
        {this.nativeScrollbarWidth ? (
          <ScrollTrack nodeRef={this.track} width={width}>
            <ScrollThumb nodeRef={this.thumb} />
          </ScrollTrack>
        ) : null}
      </ScrollContainer>
    );
  }
}

export default Scrollbar;
